import React, { useEffect, useState } from 'react';
import { Item, AccountDetail, SkuItem } from '../types';
import {
  getItems,
  getAccountDetails,
  syncItemsFromAccount,
  fetchItemSku,
  updateItem,
  updateItemMultiSpec,
  updateItemMultiQuantityDelivery
} from '../services/api';
import { itemCoverFallbackUrl } from '../lib/itemCover';
import { Box, RefreshCw, ShoppingBag, Edit, Trash2, Plus, X, FileText } from 'lucide-react';

const ItemCover: React.FC<{ src: string }> = ({ src }) => {
  const [url, setUrl] = useState(src);
  useEffect(() => {
    setUrl(src);
  }, [src]);

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-300">
        <Box className="w-10 h-10" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      onError={() => {
        const fb = itemCoverFallbackUrl(url);
        if (fb && fb !== url) setUrl(fb);
      }}
    />
  );
};

const priceLabel = (p?: string) => {
  const s = (p || '').toString().trim();
  if (!s) return '—';
  return s.startsWith('¥') ? s : `¥${s}`;
};

const parseSkuInfo = (raw?: string | SkuItem[]): SkuItem[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
};

const skuPriceLabel = (price?: number) => {
  if (price == null) return '';
  return `¥${(price / 100).toFixed(2)}`;
};

const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [editForm, setEditForm] = useState<Partial<Item>>({});
  const [addForm, setAddForm] = useState({
    cookie_id: '',
    item_id: '',
    item_title: '',
    item_price: '',
    item_image: '',
    is_multi_spec: false,
    is_multi_qty_ship: false
  });
  const [descModalItem, setDescModalItem] = useState<Item | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    getAccountDetails().then(setAccounts);
    getItems().then(setItems);
  }, []);

  const handleSync = async () => {
      if (!selectedAccount) return alert('请先选择账号');
      setLoading(true);
      try {
        const result = await syncItemsFromAccount(selectedAccount);
        if (result && result.success === false) {
          alert(`同步失败: ${result.message || '未知错误'}`);
        } else {
          alert(`同步成功: ${result?.message || `共 ${result?.total_count || 0} 件商品`}`);
        }
      } catch (e: any) {
        alert(`同步异常: ${e.message || e}`);
      }
      getItems().then(setItems);
      setLoading(false);
  };

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setEditForm({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    setEditSaving(true);
    try {
      await updateItem(selectedItem.cookie_id, selectedItem.item_id, {
        item_title: editForm.item_title ?? undefined,
        item_price: editForm.item_price ?? undefined,
        item_description: editForm.item_description ?? undefined,
        item_image: editForm.item_image ?? undefined,
      });
      const list = await getItems();
      setItems(list);
      setShowEditModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('更新商品失败:', error);
      alert('更新失败，请重试');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (item: Item) => {
    if (confirm(`确认删除商品"${item.item_title}"吗？`)) {
      try {
        const filteredItems = items.filter(i =>
          !(i.cookie_id === item.cookie_id && i.item_id === item.item_id)
        );
        setItems(filteredItems);
      } catch (error) {
        console.error('删除商品失败:', error);
        alert('删除失败，请重试');
      }
    }
  };

  const handleAddItem = async () => {
    try {
      const newItem: Item = {
        ...addForm,
        id: Date.now().toString()
      } as Item;
      setItems([newItem, ...items]);
      setShowAddModal(false);
      setAddForm({
        cookie_id: '',
        item_id: '',
        item_title: '',
        item_price: '',
        item_image: '',
        is_multi_spec: false,
        is_multi_qty_ship: false
      });
    } catch (error) {
      console.error('添加商品失败:', error);
      alert('添加失败，请重试');
    }
  };

  const toggleMultiSpec = async (item: Item) => {
    const next = !item.is_multi_spec;
    try {
      await updateItemMultiSpec(item.cookie_id, item.item_id, next);
      setItems((prev) =>
        prev.map((i) =>
          i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_spec: next } : i
        )
      );
    } catch (error) {
      console.error('更新多规格状态失败:', error);
      alert('保存失败：商品可能尚未同步到数据库，请先同步商品后再试。');
    }
  };

  const toggleMultiQty = async (item: Item) => {
    const next = !item.is_multi_qty_ship;
    try {
      await updateItemMultiQuantityDelivery(item.cookie_id, item.item_id, next);
      setItems((prev) =>
        prev.map((i) =>
          i.cookie_id === item.cookie_id && i.item_id === item.item_id ? { ...i, is_multi_qty_ship: next } : i
        )
      );
    } catch (error) {
      console.error('更新多数量发货失败:', error);
      alert('保存失败：商品可能尚未同步到数据库，请先同步商品后再试。');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">商品管理</h2>
          <p className="text-gray-500 mt-2 text-sm">监控并管理所有账号下的闲鱼商品。</p>
        </div>
        <div className="flex gap-3">
            <select
                className="ios-input px-4 py-3 rounded-xl text-sm"
                value={selectedAccount}
                onChange={e => setSelectedAccount(e.target.value)}
            >
                <option value="">选择账号以同步</option>
                {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nickname}</option>
                ))}
            </select>
            <button
                onClick={handleSync}
                disabled={loading || !selectedAccount}
                className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                同步商品
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-5 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:bg-gray-800 transition-colors flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-4 h-4" />
              添加商品
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map(item => (
              <div key={`${item.cookie_id}-${item.item_id}`} className="ios-card p-4 rounded-3xl hover:shadow-lg transition-all group relative">
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-[#FFE815] transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white/90 backdrop-blur rounded-lg shadow-md hover:bg-red-100 text-red-500 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-2xl mb-4 overflow-hidden relative">
                      <ItemCover src={item.item_image || ''} />
                  </div>
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-sm mb-1 min-h-[2.5rem]">{item.item_title}</h3>
                  <div className="mb-2 space-y-1">
                      <p className="text-sm text-gray-800 px-0.5">
                        <span className="font-bold">{priceLabel(item.item_price)}</span>
                        <span
                          className="text-[11px] text-gray-400 font-normal ml-1.5"
                          title="来自闲鱼在售列表接口，可能含红包、券后价等"
                        >
                          列表价
                        </span>
                      </p>
                  </div>
                  {(() => {
                    const skus = parseSkuInfo(item.sku_info);
                    if (skus.length === 0) return (
                      <button
                        type="button"
                        className="w-full mb-2 text-xs font-bold px-2 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-blue-200 text-blue-600 hover:bg-blue-50"
                        onClick={async () => {
                          try {
                            const res = await fetchItemSku(item.cookie_id, item.item_id);
                            if (res?.success && res.sku_list?.length > 0) {
                              alert(`获取成功: ${res.sku_list.length} 个规格`);
                              getItems().then(setItems);
                            } else {
                              alert(res?.message || '获取规格失败');
                            }
                          } catch (e: any) {
                            alert(`获取规格异常: ${e.message || e}`);
                          }
                        }}
                      >
                        <Box className="w-3.5 h-3.5" />
                        获取SKU规格
                      </button>
                    );
                    return (
                      <div className="mb-2 bg-blue-50/80 rounded-xl px-3 py-2 border border-blue-100">
                        <p className="text-[10px] text-blue-600 font-bold mb-1">SKU规格 ({skus.length})</p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {skus.map((sku, idx) => (
                            <div key={sku.skuId ?? idx} className="flex items-center justify-between text-[11px] text-gray-700">
                              <span className="truncate flex-1">
                                {sku.properties?.map(p => `${p.name}: ${p.value}`).join(' / ') || '默认'}
                              </span>
                              <span className="ml-2 shrink-0 font-semibold text-blue-700">
                                {skuPriceLabel(sku.priceInCent ?? sku.price)}
                              </span>
                              <span className="ml-1.5 shrink-0 text-gray-400">×{sku.quantity ?? 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span className="bg-gray-100 px-2 py-1 rounded-md truncate max-w-[100px]">ID: {item.item_id}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDescModalItem(item)}
                    disabled={!item.item_description?.trim()}
                    className="w-full mb-2 text-xs font-bold px-2 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {item.item_description?.trim() ? '查看商品描述' : '暂无描述'}
                  </button>
                  <div className="flex gap-2">
                      <button
                        onClick={() => toggleMultiSpec(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_spec
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多规格
                      </button>
                      <button
                        onClick={() => toggleMultiQty(item)}
                        className={`flex-1 text-xs font-bold px-2 py-1.5 rounded-lg transition-colors ${
                          item.is_multi_qty_ship
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        多数量发货
                      </button>
                  </div>
              </div>
          ))}
          {items.length === 0 && (
             <div className="col-span-full py-20 text-center text-gray-400">
                 <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                 暂无商品数据，请选择账号进行同步
             </div>
          )}
      </div>

      {showEditModal && selectedItem && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45"
          role="presentation"
          onClick={() => !editSaving && setShowEditModal(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-item-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 id="edit-item-title" className="font-bold text-gray-900">编辑商品</h3>
              <button
                type="button"
                disabled={editSaving}
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 disabled:opacity-50"
                aria-label="关闭"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto max-h-[calc(90vh-8rem)]">
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">标题</label>
                <input
                  className="ios-input w-full px-3 py-2 rounded-xl text-sm"
                  value={editForm.item_title ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, item_title: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">列表展示价（如 ¥0.98）</label>
                <input
                  className="ios-input w-full px-3 py-2 rounded-xl text-sm"
                  value={editForm.item_price ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, item_price: e.target.value }))}
                  placeholder="¥0.98"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">封面图 URL</label>
                <input
                  className="ios-input w-full px-3 py-2 rounded-xl text-sm"
                  value={editForm.item_image ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, item_image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">商品描述（套餐价会从此处解析）</label>
                <textarea
                  className="ios-input w-full px-3 py-2 rounded-xl text-sm min-h-[160px] resize-y"
                  value={editForm.item_description ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, item_description: e.target.value }))}
                />
              </div>
              <p className="text-[11px] text-gray-400">商品 ID：{selectedItem.item_id}（不可改）</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                type="button"
                disabled={editSaving}
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={editSaving}
                onClick={() => void handleSaveEdit()}
                className="ios-btn-primary px-5 py-2 rounded-xl text-sm font-bold disabled:opacity-50"
              >
                {editSaving ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {descModalItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45"
          role="presentation"
          onClick={() => setDescModalItem(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl border border-gray-100"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-start gap-3">
              <h3 className="font-bold text-gray-900 text-sm leading-snug">{descModalItem.item_title}</h3>
              <button
                type="button"
                onClick={() => setDescModalItem(null)}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 shrink-0"
                aria-label="关闭"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto max-h-[65vh] text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {descModalItem.item_description?.trim() || '暂无描述'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemList;
